/**
 * Ticket 96: "If the user's total volume exceeds a reasonable threshold, please
 * log this somewhere and we can revisit it as to whether we want to cap the
 * volume or something. For right now just provide the functionality and we
 * won't worry about people intentionally blasting each other with volume."
 *
 * So this deliberately CAPS NOTHING. It decides what to say and how often, and
 * the caller ships it through the room's existing client-log pipeline — no new
 * endpoint, and therefore nothing new to rate-limit.
 *
 * It is deduplicated because a range input fires continuously while it is
 * dragged: the naive version ships a hundred lines for one gesture and spends
 * the whole 3-second flush buffer on a single person fidgeting with a slider.
 */

/**
 * The input level above which a boost is worth recording. 2 = publishing twice
 * as loud as the microphone captured, which nothing about ordinary speech
 * needs. Below this there is nothing to revisit.
 */
export const MIC_GAIN_LOG_THRESHOLD = 2;

export interface MicGainObservation {
  /** Who is boosting their own microphone. */
  accountId: string;
  /** The input level as a multiplier: 1 = as captured, 3 = the ceiling. */
  level: number;
  /** False when the browser has no Web Audio, so the boost did not happen. */
  applied: boolean;
}

const asPercent = (level: number): number => Math.round(level * 100);

/**
 * One decimal is the dedupe grain: fine enough to watch someone climb from 2x
 * to 3x, coarse enough that a full drag is ~10 lines rather than ~100.
 */
const toGrain = (level: number): number => Math.round(level * 10) / 10;

/**
 * The line to log, or null when there is nothing new to say.
 *
 * `seen` is the caller's memory. Someone who drops back under the threshold is
 * forgotten, so a later re-boost is reported afresh rather than suppressed for
 * the remaining lifetime of the room.
 */
export const micGainLogLine = (
  observation: MicGainObservation,
  seen: Map<string, number>,
): string | null => {
  const { accountId, level, applied } = observation;

  if (!Number.isFinite(level) || level <= MIC_GAIN_LOG_THRESHOLD) {
    seen.delete(accountId);
    return null;
  }

  const grain = toGrain(level);
  if (seen.get(accountId) === grain) return null;
  seen.set(accountId, grain);

  // Greppable prefix, and every number someone deciding whether to cap this
  // would want: who, how loud, and whether the boost actually took effect.
  // Deliberately worded without any of the keywords that force an immediate log
  // flush in useRoomAudio — a loud microphone is not an incident.
  return `[Room] MIC_GAIN_BOOST account=${accountId} input=${asPercent(
    level,
  )}% level=${grain}x applied=${applied ? 'yes' : 'no'}`;
};
